import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    audioList,
    devicesList,
    getDeviceAudioPlaylist,
    attachAudioToDevice,
    detachAudioFromDevice,
    reorderDeviceAudio,
} from '../api/audio';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import './AudioPlaylists.scss';

export default function AudioPlaylistDetail() {
    const { id } = useParams();            // id = deviceId
    const deviceId = Number(id || 0);

    const [loading, setLoading] = useState(false);
    const [playlist, setPlaylist] = useState(null); // {device:{id,code}, items:[{id,position,audio:{...}}]}
    const [allAudio, setAllAudio] = useState([]);
    const [devices, setDevices] = useState([]);
    const [addingId, setAddingId] = useState(null);
    const [addingAll, setAddingAll] = useState(false);
    const [addedCount, setAddedCount] = useState(0);
    const [savingOrder, setSavingOrder] = useState(false);

    const existingAudioIds = useMemo(
        () => new Set((playlist?.items || []).map(i => i.audio?.id)),
        [playlist]
    );

    useEffect(() => { if (deviceId) load(); }, [deviceId]);
    async function load() {
        setLoading(true);
        try {
            const [pl, aud, dev] = await Promise.all([
                getDeviceAudioPlaylist(deviceId),
                audioList(),
                devicesList()
            ]);
            setPlaylist(pl);     // { device, items }
            setAllAudio(aud);
            setDevices(dev);
        } catch (e) {
            alert(e.message);
        } finally {
            setLoading(false);
        }
    }

    async function add(audioId) {
        setAddingId(audioId);
        try {
            await attachAudioToDevice(deviceId, audioId); // в конец
            await load();
        } catch (e) {
            alert(e.message);
        } finally {
            setAddingId(null);
        }
    }

    async function addAll() {
        if (!playlist) return;
        const toAdd = allAudio
            .filter(a => !existingAudioIds.has(a.id))
            .map(a => a.id);

        if (toAdd.length === 0) {
            alert('Все треки уже в плейлисте');
            return;
        }

        setAddingAll(true);
        setAddedCount(0);
        try {
            // Последовательно, чтобы не штурмовать бэкенд и видеть прогресс
            for (let i = 0; i < toAdd.length; i++) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await attachAudioToDevice(deviceId, toAdd[i]);
                    setAddedCount(i + 1);
                } catch (e) {
                    // продолжаем дальше, просто сообщим в конце
                    // можно логгировать e
                }
            }
            await load();
        } finally {
            setAddingAll(false);
        }
    }

    async function removeItem(audioId) {
        try {
            await detachAudioFromDevice(deviceId, Number(audioId));
            await load();
        } catch (e) {
            alert(e.message);
        }
    }

    function onDragEnd(result) {
        if (!result.destination || !playlist) return;
        const items = Array.from(playlist.items || []);
        const [rem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, rem);
        setPlaylist({ ...playlist, items });
    }

    async function saveOrder() {
        if (!playlist?.items) return;
        setSavingOrder(true);
        try {
            const orders = playlist.items.map((it, idx) => ({ audioId: it.audio.id, position: idx }));
            await reorderDeviceAudio(deviceId, orders);
        } catch (e) {
            alert(e.message);
        } finally {
            setSavingOrder(false);
        }
    }

    return (
        <div className="a-pl-detail">
            {loading ? <div className="loading">Загрузка...</div> : playlist && (
                <>
                    <div className="head">
                        <h2>Аудио плейлист устройства #{playlist.device?.id} (код {playlist.device?.code})</h2>
                        <div className="head-actions">
                            <button className="primary" disabled={savingOrder} onClick={saveOrder}>
                                {savingOrder ? 'Сохраняю...' : 'Сохранить порядок'}
                            </button>
                        </div>
                    </div>

                    <div className="columns">
                        {/* Треки плейлиста */}
                        <div className="col">
                            <h3>Треки плейлиста</h3>
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="tracks">
                                    {(provided) => (
                                        <div className="list playlist" ref={provided.innerRef} {...provided.droppableProps}>
                                            {(playlist.items || []).length === 0 && <div className="empty">Пусто</div>}
                                            {(playlist.items || []).map((it, idx) => (
                                                <Draggable draggableId={`audio-${it.audio.id}`} index={idx} key={`audio-${it.audio.id}`}>
                                                    {(pp, snap) => (
                                                        <div
                                                            className={`row ${snap.isDragging ? 'dragging':''}`}
                                                            ref={pp.innerRef}
                                                            {...pp.draggableProps}
                                                            {...pp.dragHandleProps}
                                                        >
                                                            <div className="row-main">
                                                                <div className="title">{it.audio?.filename || `#${it.audio?.id}`}</div>
                                                                <div className="meta">id:{it.audio?.id} • hash:{it.audio?.hash}</div>
                                                            </div>
                                                            <div className="row-actions">
                                                                <button className="danger" onClick={() => removeItem(it.audio.id)}>Удалить</button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>

                        {/* Добавление треков */}
                        <div className="col">
                            <div className="col-head">
                                <h3>Добавить трек</h3>
                                <div className="col-actions">
                                    <button
                                        className="secondary"
                                        onClick={addAll}
                                        disabled={addingAll || allAudio.length === 0 || existingAudioIds.size === allAudio.length}
                                        title="Добавить все треки из библиотеки в конец плейлиста"
                                    >
                                        {addingAll ? `Добавляю... (${addedCount})` : 'Добавить все'}
                                    </button>
                                </div>
                            </div>
                            <div className="list library">
                                {allAudio.length === 0 ? <div className="empty">Нет аудиофайлов</div> :
                                    allAudio.map(a => {
                                        const already = existingAudioIds.has(a.id);
                                        return (
                                            <div key={a.id} className={`row ${already ? 'disabled' : ''}`}>
                                                <div className="row-main">
                                                    <div className="title">{a.filename || `#${a.id}`}</div>
                                                    <div className="meta">
                                                        id:{a.id} • {(a.size ? (a.size/1024/1024).toFixed(2) : '0.00')} MB
                                                        {already ? ' • в плейлисте' : ''}
                                                    </div>
                                                </div>
                                                <div className="row-actions">
                                                    <button
                                                        className="primary"
                                                        disabled={addingId===a.id || already || addingAll}
                                                        onClick={() => add(a.id)}
                                                    >
                                                        {addingId===a.id ? 'Добавляю...' : 'Добавить'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>

                        {/* Справочно: устройства */}
                        <div className="col">
                            <h3>Устройства</h3>
                            <div className="list devices">
                                {devices.length === 0 ? <div className="empty">Нет устройств</div> :
                                    devices.map(d => (
                                        <div key={d.id} className="row">
                                            <div className="row-main">
                                                <div className="title">Устройство #{d.id}</div>
                                                <div className="meta">код: {d.code}</div>
                                            </div>
                                            <div className="row-actions">
                                                {d.id === playlist.device?.id ? <span>текущие</span> : null}
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
